import { createApi } from "@reduxjs/toolkit/query/react";
import { createBaseApiWithAuth } from "@/utilities/reduxToolkit";
import type {
  DealListItem,
  DealsSummary as DealsSummaryBase,
} from "@/pages/Deals/dealsList.types";

/** Per-stage aggregate for the home pipeline funnel. */
export interface DealsSummaryStageAggregate {
  count: number;
  value: number;
}

/**
 * `GET /deals/summary` projection used app-wide. Additively extends the
 * Deals-list `DealsSummary` with `byStage` — a `{ StepCategory → {count,
 * value} }` map computed server-side (one `groupBy` over current
 * `StepCategory`, same active-book scope as the existing metrics) so the home
 * pipeline funnel is accurate and cheap instead of bucketing a paginated page
 * client-side. Optional for backcompat with callers/servers that don't send it.
 */
export interface DealsSummary extends DealsSummaryBase {
  byStage?: Record<string, DealsSummaryStageAggregate>;
}

/**
 * Default include for the Deals List overhaul. Brings the existing
 * contributors + property thumbnails plus the new `listingSheet` projection
 * and pursuit `_count` the backend now returns. Callers may override.
 */
export const DEALS_LIST_INCLUDE =
  "contributors,propertyAssets,listingSheet,pursuitCount";

// Types
interface DealMember {
  id: string;
  name: string;
  role: string;
  roleId?: string;
  dealTeamId?: string;
  // REP-192 — per-deal display alias; person stays the signatory.
  displayName?: string | null;
}

interface AddTeamToDealRequest {
  dealId: string;
  members: DealMember[];
  /** REP-204 stage-scoped access — grant the added people full document
   *  history instead of flooring them at the deal's current step. */
  includeHistory?: boolean;
}

interface AddTeamToDealResponse {
  data: {
    added: number;
    message: string;
  };
}

export const dealsApi = createApi({
  ...createBaseApiWithAuth({
    reducerPath: "dealsApi",
    baseUrl: "/api/deals",
  }),
  reducerPath: "dealsApi",
  tagTypes: ["Deals", "Fields"],
  endpoints: (builder) => ({
    getDeals: builder.query<
      { data: { data: DealListItem[]; nextId?: string | null } },
      {
        include?: string;
        search?: string;
        nextId?: string;
        limit?: number;
        status?: string;
        /** Comma-separated ProjectType values. Defaults backend-side to no
         *  filter, but the deals list page restricts to deal-types only and
         *  lets users opt into rep-agreement project types. */
        projectType?: string;
      }
    >({
      query: (args) => {
        // Build query parameters dynamically
        const queryParams = new URLSearchParams();
        // Append parameters if they exist
        if (args.search) queryParams.append("query", args.search);
        if (args.include) queryParams.append("include", args.include);
        if (args.limit) queryParams.append("limit", args.limit.toString());
        if (args.nextId) queryParams.append("nextId", args.nextId);
        if (args.status) queryParams.append("status", args.status);
        if (args.projectType) queryParams.append("projectType", args.projectType);
        return `?${queryParams.toString()}`;
      },
      keepUnusedDataFor: 0,
      providesTags: [{ type: "Deals", id: "LIST" }],
    }),
    /**
     * GET /api/deals/summary — aggregate metrics over the current user's
     * ACTIVE book (pipeline value + counts). Computed server-side so the
     * metrics strip never sums a paginated page client-side.
     */
    getDealsSummary: builder.query<{ data: DealsSummary }, void>({
      query: () => `/summary`,
      keepUnusedDataFor: 30,
      providesTags: [{ type: "Deals", id: "LIST" }],
    }),
    /**
     * NOTE: `include` is part of the cache key (see providesTags/query below),
     * so two components asking for the same deal with different include
     * strings produce two separate cache entries and two separate HTTP
     * requests — each of which is an expensive server-side tree walk.
     *
     * Anything rendering inside DealLayout should pass DEAL_PAGE_INCLUDE so the
     * whole deal page shares one entry. See its definition below.
     */
    getDealById: builder.query<any, { id?: string; include: string }>({
      query: (args) => {
        const queryParams = args.include ? `?include=${args.include}` : "";
        return `/${args.id}${queryParams}`;
      },
      // @ts-ignore
      providesTags: (result, error, args) => [{ type: "Deals", id: args.id }],
    }),
    createDeal: builder.mutation<any, any>({
      query: (body) => ({
        url: "/",
        method: "POST",
        body: body,
      }),
      // Creates a new deal → only list membership changes. Scoped to LIST so
      // this no longer evicts every cached deal detail page.
      invalidatesTags: [{ type: "Deals", id: "LIST" }],
    }),
    /**
     * POST /api/deals/buyer-rep
     *
     * Creates a BUYER_REP_AGREEMENT project + a draft ClientNeed linked to
     * it. The ClientNeed comes back empty — fill it in on the onboarding
     * page via PUT /client-needs/:id.
     */
    createBuyerRepAgreement: builder.mutation<
      any,
      {
        internalName: string;
        personId: string;
        companyId?: string;
        title?: string;
        postalCode?: string;
        /** Which hat the creator wears on this deal — seeds their role. */
        creatorRole?: "AGENT" | "BROKER";
        /** The person filling the OTHER our-side seat (agent or broker). */
        counterpartPersonId?: string;
      }
    >({
      query: (body) => ({
        url: "/buyer-rep",
        method: "POST",
        body,
      }),
      // Creates a new deal → only list membership changes. Scoped to LIST so
      // this no longer evicts every cached deal detail page.
      invalidatesTags: [{ type: "Deals", id: "LIST" }],
    }),
    /**
     * POST /api/deals/tenant-rep
     *
     * Demand-side lease analogue of buyer-rep. Creates a
     * TENANT_REP_AGREEMENT project + a draft ClientNeed linked to it for
     * a tenant looking to lease space. Fill in the need on the onboarding
     * page via PUT /client-needs/:id.
     */
    createTenantRepAgreement: builder.mutation<
      any,
      {
        internalName: string;
        personId: string;
        companyId?: string;
        title?: string;
        postalCode?: string;
        /** Which hat the creator wears on this deal — seeds their role. */
        creatorRole?: "AGENT" | "BROKER";
        /** The person filling the OTHER our-side seat (agent or broker). */
        counterpartPersonId?: string;
      }
    >({
      query: (body) => ({
        url: "/tenant-rep",
        method: "POST",
        body,
      }),
      // Creates a new deal → only list membership changes. Scoped to LIST so
      // this no longer evicts every cached deal detail page.
      invalidatesTags: [{ type: "Deals", id: "LIST" }],
    }),
    /**
     * POST /api/deals/seller-rep
     *
     * Creates a SELLER_REP_AGREEMENT project + a draft ListingSheet tied
     * to the property. Fill / publish the sheet on the onboarding page.
     *
     * `personId` is optional — CRM Person view passes it, Property view
     * omits it (broker adds the seller later from Parties).
     */
    createSellerRepAgreement: builder.mutation<
      any,
      {
        internalName: string;
        propertyId: string;
        title?: string;
        personId?: string;
        // REP-216 — adopt an existing standalone listing instead of minting a draft.
        adoptListingId?: string;
        /** Which hat the creator wears on this deal — seeds their role. */
        creatorRole?: "AGENT" | "BROKER";
        /** The person filling the OTHER our-side seat (agent or broker). */
        counterpartPersonId?: string;
      }
    >({
      query: (body) => ({
        url: "/seller-rep",
        method: "POST",
        body,
      }),
      // Creates a new deal → only list membership changes. Scoped to LIST so
      // this no longer evicts every cached deal detail page.
      invalidatesTags: [{ type: "Deals", id: "LIST" }],
    }),
    /**
     * POST /api/deals/landlord-rep
     *
     * Supply-side lease analogue of seller-rep. Creates a
     * LANDLORD_REP_AGREEMENT project + a draft ListingSheet tied to the
     * property with lease economics. Fill / publish the sheet on the
     * onboarding page.
     *
     * `personId` is optional — CRM Person view passes it, Property view
     * omits it (broker adds the landlord later from Parties).
     */
    createLandlordRepAgreement: builder.mutation<
      any,
      {
        internalName: string;
        propertyId: string;
        title?: string;
        personId?: string;
        // REP-216 — adopt an existing standalone listing instead of minting a draft.
        adoptListingId?: string;
        /** Which hat the creator wears on this deal — seeds their role. */
        creatorRole?: "AGENT" | "BROKER";
        /** The person filling the OTHER our-side seat (agent or broker). */
        counterpartPersonId?: string;
      }
    >({
      query: (body) => ({
        url: "/landlord-rep",
        method: "POST",
        body,
      }),
      // Creates a new deal → only list membership changes. Scoped to LIST so
      // this no longer evicts every cached deal detail page.
      invalidatesTags: [{ type: "Deals", id: "LIST" }],
    }),
    /**
     * POST /api/deals/standalone
     *
     * REP-199 Phase 4 — "continuous" deals (Path B). Creates a root
     * LEASE_DEAL or SALE_DEAL Project. Person + property are optional;
     * onboarding (rate, term, etc.) is filled inside the deal page.
     */
    createStandaloneDeal: builder.mutation<
      any,
      {
        projectType: "LEASE_DEAL" | "SALE_DEAL";
        internalName?: string;
        personId?: string;
        propertyId?: string;
      }
    >({
      query: (body) => ({
        url: "/standalone",
        method: "POST",
        body,
      }),
      // Creates a new deal → only list membership changes. Scoped to LIST so
      // this no longer evicts every cached deal detail page.
      invalidatesTags: [{ type: "Deals", id: "LIST" }],
    }),
    /**
     * POST /api/deals/spin-off
     *
     * Unified "Start deal" / "Add to deal" entry point (REP-200 §6).
     *
     * Accepts the new multi-shape `{ fromProjectId, propertyIds }` and
     * the legacy single-property `{ propertyId, fromProjectId }` shape.
     * Returns `{ parentDealId, pursuits, deal }`.
     */
    spinOffDeal: builder.mutation<
      any,
      {
        fromProjectId: string;
        propertyIds?: string[];
        propertyId?: string;
        /** New pursuit's name — used on creation only (ignored if the parent
         *  already exists; server falls back to the derived default if empty). */
        parentName?: string;
        /** Deal Team Formation — name for the merged OUR_SIDE "deal squad" and
         *  any extra members added at the Create-Deal prompt. Applied when the
         *  parent is first created; server derives a default name if empty. */
        squadName?: string;
        additionalMembers?: Array<{ personId: string; roleId?: string }>;
      }
    >({
      query: ({ parentName, squadName, additionalMembers, ...body }) => ({
        url: "/spin-off",
        method: "POST",
        body: {
          ...body,
          ...(parentName?.trim() ? { parentName } : {}),
          ...(squadName?.trim() ? { squadName } : {}),
          ...(additionalMembers && additionalMembers.length ? { additionalMembers } : {}),
        },
      }),
      // Creates a new deal → only list membership changes. Scoped to LIST so
      // this no longer evicts every cached deal detail page.
      invalidatesTags: [{ type: "Deals", id: "LIST" }],
    }),
    /**
     * GET /api/deals?addableForUser=true — UnifiedDealPicker dataset.
     * Returns parent deals + rep agreements not yet spun off into a
     * parent. Used by Agents A and B via the shared UnifiedDealPicker.
     */
    getAddableDeals: builder.query<
      {
        data: {
          items: Array<{
            kind: "parent_deal" | "rep_agreement";
            id: string;
            name: string;
            projectType: string;
            pursuitCount?: number;
            lastActivityAt?: string | null;
            signedAt?: string | null;
            notYetStarted?: boolean;
          }>;
        };
      },
      void
    >({
      query: () => `?addableForUser=true`,
      keepUnusedDataFor: 30,
      providesTags: [{ type: "Deals", id: "LIST" }],
    }),
    /**
     * REP-200 §8 — Active-deal pin (primary pursuit). Set/clear write
     * to `ProjectRelationship.is_primary`; one primary per parent.
     */
    setPrimaryPursuit: builder.mutation<
      any,
      { dealId: string; pursuitId: string }
    >({
      query: ({ dealId, pursuitId }) => ({
        url: `/${dealId}/primary-pursuit`,
        method: "POST",
        body: { pursuitId },
      }),
      invalidatesTags: (_r, _e, args) => [
        { type: "Deals", id: args.dealId },
        { type: "Deals" },
      ],
    }),
    clearPrimaryPursuit: builder.mutation<any, { dealId: string }>({
      query: ({ dealId }) => ({
        url: `/${dealId}/primary-pursuit`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, args) => [
        { type: "Deals", id: args.dealId },
        { type: "Deals" },
      ],
    }),
    deleteDeal: builder.mutation<void, string>({
      query: (id) => ({
        url: `/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, id) => [{ type: "Deals", id }, { type: "Deals", id: "LIST" }],
    }),
    addTeamToDeal: builder.mutation<
      AddTeamToDealResponse,
      AddTeamToDealRequest
    >({
      query: ({ dealId, members, includeHistory }) => ({
        url: `/${dealId}/teams`,
        method: "POST",
        body: { members, includeHistory },
      }),
      invalidatesTags: (_r, _e, { dealId }) => [{ type: "Deals", id: dealId }],
    }),
    createDealTeam: builder.mutation<any, { dealId: string; name: string }>({
      query: ({ dealId, name }) => ({
        url: `/${dealId}/teams/create`,
        method: "POST",
        body: { name },
      }),
      invalidatesTags: (_r, _e, { dealId }) => [{ type: "Deals", id: dealId }],
    }),
    getMissingFieldsForDeal: builder.query<
      any,
      { id?: string; status?: string }
    >({
      query: (args) =>
        `/${args.id}/documents/fields?status=${
          args.status ? args.status : "UNRESOLVED"
        }`,
      keepUnusedDataFor: 0,
      providesTags: [{ type: "Fields" }],
    }),
  }),
});

/**
 * The canonical `include` for every getDealById call made from inside the deal
 * page (DealLayout and anything it renders).
 *
 * It's the union of what those views individually needed. Sharing one string
 * means one cache entry and ONE request for the whole page; previously
 * DealLayout, DealOverview, DealParties, DealActivity and DealOutreach each
 * passed a different string, so navigating between deal tabs fired a fresh
 * full-tree fetch every time with zero reuse.
 *
 * DealLayout wraps all of these, so the union is fetched regardless — the only
 * thing the divergent strings bought was cache misses.
 */
export const DEAL_PAGE_INCLUDE =
  "properties,propertyAssets,propertyAddresses,workflow,events,teams,sources,pursuits,parentDeal,activeDeal,contributors";

export const {
  useGetDealsQuery,
  useLazyGetDealsQuery,
  useGetDealsSummaryQuery,
  useGetDealByIdQuery,
  useCreateDealMutation,
  useCreateBuyerRepAgreementMutation,
  useCreateTenantRepAgreementMutation,
  useCreateSellerRepAgreementMutation,
  useCreateLandlordRepAgreementMutation,
  useCreateStandaloneDealMutation,
  useSpinOffDealMutation,
  useGetAddableDealsQuery,
  useLazyGetAddableDealsQuery,
  useSetPrimaryPursuitMutation,
  useClearPrimaryPursuitMutation,
  useDeleteDealMutation,
  useAddTeamToDealMutation,
  useCreateDealTeamMutation,
  useGetMissingFieldsForDealQuery,
} = dealsApi;
